import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  ImagePlus,
  Image as ImageIcon,
  Film,
  FileText,
  X,
  Check,
  ArrowUp,
  RotateCw,
  FileWarning,
} from 'lucide-react';
import { formatBytes } from './photoUploadUtils';

// Simulated upload duration for pages that do not pass an `uploadFn` (pure
// frontend preview). When `uploadFn` is provided the file is really uploaded
// and the bar eases toward REAL_UPLOAD_EASE_CAP while pending.
const SIMULATED_UPLOAD_MS = 1600;

// Eased progress cap while a real upload is pending. The bar only reaches
// 100% when the upload promise resolves, so "done" tracks the server result.
const REAL_UPLOAD_EASE_CAP = 90;

// Number of latest image/video files that keep an expanded preview. Older
// media files and documents render as collapsed panels without a preview.
const MAX_EXPANDED_PREVIEWS = 2;

// Duration (ms) of the remove "rollback" animation. Must match the
// `photo-upload-revert` keyframe duration in _photoUpload.scss.
const REMOVE_ANIMATION_MS = 240;

const DEFAULT_ACCEPT = {
  'image/*': [],
  'video/*': [],
  'application/pdf': [],
  'text/*': [],
  'application/msword': [],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
  'application/vnd.ms-excel': [],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
};

let uidCounter = 0;

function nextUid() {
  uidCounter += 1;
  return `photo_${Date.now().toString(36)}_${uidCounter.toString(36)}`;
}

function isImage(file) {
  return file.type && file.type.startsWith('image/');
}

function isVideo(file) {
  return file.type && file.type.startsWith('video/');
}

function isMedia(file) {
  return isImage(file) || isVideo(file);
}

// Loose accept matching for pasted files: wildcard mime types (image/*),
// exact mimes, and dot-extension entries.
function matchesAccept(file, accept) {
  if (!accept) return true;
  const type = file.type || '';
  for (const key of Object.keys(accept)) {
    if (key.startsWith('.') && file.name.toLowerCase().endsWith(key.toLowerCase())) return true;
    if (type && key.includes('/')) {
      const [category, subType] = key.split('/');
      const [fileCategory, fileSubType] = type.split('/');
      if (category === fileCategory && (subType === '*' || subType === fileSubType)) return true;
    }
  }
  return false;
}

/**
 * Controlled multi-file upload built on react-dropzone.
 *
 * Features: click to select, drag & drop, paste from clipboard (Ctrl+V),
 * FilePond-style preview panels, simulated upload progress, per-file remove
 * with a rollback animation, and re-upload after completion.
 *
 * One panel is rendered per file, each exactly the width of the dropzone
 * container. Image/video files show an expanded preview (only the latest 2);
 * documents and older media render as collapsed panels without a preview.
 * The header action button shows an upload arrow while processing and flips
 * to a re-upload (circular arrow) once the file is complete.
 *
 * Props:
 *   files      File[]       current files (controlled)
 *   previews   string[]     object URLs aligned with `files`
 *   onChange   (files)      called with the next file list
 *   onRemove   (index)      called when a file's remove button is pressed
 *   accept     accept map   which file types are allowed (gated by the form)
 *   label      string       field label rendered above the dropzone
 *   helperText string       muted hint rendered under the dropzone
 *   error      string       inline error message
 *   disabled   boolean      disables all interactions
 *   previewHeight number    preview container height in px (default 240)
 *   showPreview boolean     show the media preview body for the latest media
 *                           files (default true). Set false to render all
 *                           panels collapsed without any image/video preview.
 *
 * Real uploads (optional): pass `uploadFn` to upload each file the moment it
 * is selected instead of simulating progress. `uploadFn(file)` must resolve
 * with the stored path (e.g. `data.path`) or reject. Per-file state then
 * cycles uploading → done / failed; the action button retries a done or failed
 * file, and the remove button deletes it. The parent is notified through:
 *   onStatusChange ({ uploading, failed }) — counts used to gate submit
 *   onPathsChange  (paths)                  — resolved path per file, aligned
 *                                             with `files` (null while pending)
 * When `uploadFn` is omitted the simulated progress is kept (demo pages).
 */
export default function PhotoUpload({
  files = [],
  previews = [],
  onChange,
  onRemove,
  uploadFn,
  onStatusChange,
  onPathsChange,
  accept = DEFAULT_ACCEPT,
  label,
  helperText,
  error,
  disabled = false,
  className = '',
  previewHeight = 240,
  showPreview = true,
  enablePaste = true,
}) {
  const [uidByFile, setUidByFile] = useState(() => new Map());
  const [runs, setRuns] = useState({});
  const [progress, setProgress] = useState({});
  const [rejectMsg, setRejectMsg] = useState(null);
  const [removing, setRemoving] = useState(() => new Set());
  const [statusByFile, setStatusByFile] = useState(() => new Map());
  const [errorsByFile, setErrorsByFile] = useState(() => new Map());
  const timers = useRef(new Map());
  const completed = useRef(new Set());
  const filesRef = useRef(files);
  const removeTimers = useRef(new Map());
  const uidByFileRef = useRef(new Map());
  const jobsByFile = useRef(new Map());
  const pathByFile = useRef(new Map());
  const onStatusChangeRef = useRef(onStatusChange);
  const onPathsChangeRef = useRef(onPathsChange);

  // Keep the latest file list available to pending remove timers.
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  // Keep uid lookups available to async upload callbacks.
  useEffect(() => {
    uidByFileRef.current = uidByFile;
  }, [uidByFile]);

  // Latest callbacks (parents may pass inline lambdas).
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  });
  useEffect(() => {
    onPathsChangeRef.current = onPathsChange;
  });

  const stopTimer = useCallback((uid) => {
    const timer = timers.current.get(uid);
    if (timer) {
      clearInterval(timer);
      timers.current.delete(uid);
    }
  }, []);

  const runUpload = useCallback(
    (file, uid) => {
      if (!uploadFn || !uid) return;
      setErrorsByFile((prev) => {
        const next = new Map(prev);
        next.delete(file);
        return next;
      });
      jobsByFile.current.set(file, true);
      setProgress((prev) => ({ ...prev, [uid]: 0 }));
      setRuns((prev) => ({ ...prev, [uid]: performance.now() }));
      setStatusByFile((prev) => new Map(prev).set(file, 'uploading'));
      Promise.resolve()
        .then(() => uploadFn(file))
        .then((path) => {
          if (!jobsByFile.current.has(file)) return;
          pathByFile.current.set(file, path ?? null);
          stopTimer(uid);
          jobsByFile.current.delete(file);
          setProgress((prev) => ({ ...prev, [uid]: 100 }));
          setStatusByFile((prev) => new Map(prev).set(file, 'done'));
        })
        .catch((err) => {
          if (!jobsByFile.current.has(file)) return;
          stopTimer(uid);
          jobsByFile.current.delete(file);
          setProgress((prev) => ({ ...prev, [uid]: 0 }));
          setStatusByFile((prev) => new Map(prev).set(file, 'failed'));
          setErrorsByFile((prev) => new Map(prev).set(file, err?.message || 'Unggah gagal'));
        });
    },
    [uploadFn, stopTimer],
  );

  // Report resolved paths aligned with the current files (re-runs on status
  // transitions because a done file may now have a path).
  useEffect(() => {
    if (!onPathsChangeRef.current) return;
    onPathsChangeRef.current(files.map((file) => pathByFile.current.get(file) ?? null));
  }, [files, statusByFile]);

  // Report uploading/failed counts so the parent can gate submit.
  useEffect(() => {
    if (!onStatusChangeRef.current) return;
    let uploading = 0;
    let failed = 0;
    for (const file of files) {
      const status = statusByFile.get(file);
      if (status === 'uploading') uploading += 1;
      else if (status === 'failed') failed += 1;
    }
    onStatusChangeRef.current({ uploading, failed });
  }, [files, statusByFile]);

  const addFiles = useCallback(
    (incoming) => {
      if (disabled || incoming.length === 0) return;
      const nextMap = new Map(uidByFile);
      const nextRuns = {};
      const now = performance.now();
      for (const file of incoming) {
        if (!nextMap.has(file)) nextMap.set(file, nextUid());
        nextRuns[nextMap.get(file)] = now;
      }
      setUidByFile(nextMap);
      setRuns((prev) => ({ ...prev, ...nextRuns }));
      setProgress((prev) => {
        const next = { ...prev };
        for (const uid of Object.keys(nextRuns)) next[uid] = 0;
        return next;
      });
      if (uploadFn) {
        setStatusByFile((prev) => {
          const next = new Map(prev);
          for (const file of incoming) next.set(file, 'uploading');
          return next;
        });
        for (const file of incoming) runUpload(file, nextMap.get(file));
      }
      onChange([...files, ...incoming]);
      setRejectMsg(null);
    },
    [files, disabled, onChange, uidByFile, uploadFn, runUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejected) => {
      addFiles(accepted);
      if (rejected.length > 0) {
        const reason = rejected[0].errors?.[0]?.message ?? 'File tidak didukung';
        setRejectMsg(`File ditolak: ${reason}`);
      }
    },
    accept,
    multiple: true,
    noKeyboard: true,
    disabled,
  });

  // Paste from clipboard (react-dropzone has no built-in paste support).
  // Only attaches a global paste listener when `enablePaste` is true, so a
  // form can restrict clipboard uploads to a single field among several.
  useEffect(() => {
    if (!enablePaste) return;
    const handlePaste = (event) => {
      if (disabled) return;
      const items = event.clipboardData?.items;
      if (!items) return;
      const pasted = [];
      let rejected = 0;
      for (const item of items) {
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (!file) continue;
        if (matchesAccept(file, accept)) pasted.push(file);
        else rejected += 1;
      }
      if (pasted.length > 0) {
        event.preventDefault();
        addFiles(pasted);
      }
      if (rejected > 0) {
        setRejectMsg(`File ditolak: ${rejected} file tidak didukung`);
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [enablePaste, disabled, addFiles, accept]);

  // Simulated/eased upload progress per file. Re-runs whenever `runs` changes,
  // which is also what triggers a re-upload after completion.
  // With `uploadFn` the bar eases to REAL_UPLOAD_EASE_CAP and only jumps to
  // 100% when the real upload resolves (see `runUpload`); without it the bar
  // runs to 100% and marks the file complete, as before.
  useEffect(() => {
    for (const uid of Object.keys(runs)) {
      if (completed.current.has(uid)) continue;
      if (timers.current.has(uid)) continue;
      const start = runs[uid];
      const step = () => {
        const elapsedPct = Math.round(
          ((performance.now() - start) / SIMULATED_UPLOAD_MS) * 100,
        );
        const pct = uploadFn
          ? Math.min(REAL_UPLOAD_EASE_CAP, elapsedPct)
          : Math.min(100, elapsedPct);
        setProgress((prev) => (prev[uid] === pct ? prev : { ...prev, [uid]: pct }));
        if (!uploadFn && pct >= 100) {
          const timer = timers.current.get(uid);
          clearInterval(timer);
          timers.current.delete(uid);
          completed.current.add(uid);
        }
      };
      timers.current.set(uid, setInterval(step, 40));
    }
  }, [runs, uploadFn]);

  // Clear all timers on unmount and drop in-flight upload jobs so late
  // resolving promises no-op instead of updating unmounted state.
  useEffect(() => {
    const timersRef = timers.current;
    const removeTimersRef = removeTimers.current;
    const jobsRef = jobsByFile.current;
    return () => {
      for (const timer of timersRef.values()) clearInterval(timer);
      for (const timer of removeTimersRef.values()) clearTimeout(timer);
      timersRef.clear();
      removeTimersRef.clear();
      jobsRef.clear();
    };
  }, []);

  // Remove a file after a short "rollback" animation, then call onRemove.
  // The index is resolved against the latest file list so staggered removals
  // don't delete the wrong file. Cleanup also drops any in-flight/pending
  // upload state so a late-resolving promise is ignored and the parent's
  // submit gate re-enables.
  const handleRemove = (uid, file) => {
    if (disabled || onRemove == null || removing.has(uid)) return;
    setRemoving((prev) => new Set(prev).add(uid));
    const timer = setTimeout(() => {
      removeTimers.current.delete(uid);
      jobsByFile.current.delete(file);
      pathByFile.current.delete(file);
      stopTimer(uid);
      setStatusByFile((prev) => {
        const next = new Map(prev);
        next.delete(file);
        return next;
      });
      setErrorsByFile((prev) => {
        const next = new Map(prev);
        next.delete(file);
        return next;
      });
      const currentIndex = filesRef.current.findIndex((f) => f === file);
      if (currentIndex !== -1) onRemove(currentIndex);
    }, REMOVE_ANIMATION_MS);
    removeTimers.current.set(uid, timer);
  };

  const handleAction = (uid, file) => {
    if (disabled) return;
    if (uploadFn) {
      const status = statusByFile.get(file);
      if (status !== 'done' && status !== 'failed') return;
      runUpload(file, uid);
      return;
    }
    if (!completed.current.has(uid)) return;
    completed.current.delete(uid);
    setProgress((prev) => ({ ...prev, [uid]: 0 }));
    setRuns((prev) => ({ ...prev, [uid]: performance.now() }));
  };

  const mediaIndexes = [];
  files.forEach((file, index) => {
    if (isMedia(file)) mediaIndexes.push(index);
  });
  const expandSet = new Set(mediaIndexes.slice(-MAX_EXPANDED_PREVIEWS));

  const hasFiles = files.length > 0;
  const hint = error || rejectMsg;

  return (
    <div
      className={`photo-upload${className ? ` ${className}` : ''}${
        isDragActive ? ' photo-upload--dragging' : ''
      }`}
      style={{ '--photo-upload-preview-height': `${previewHeight}px` }}
    >
      {label && <label className="photo-upload__label">{label}</label>}

      <div
        {...getRootProps({
          className: `photo-upload__drop${hasFiles ? ' photo-upload__drop--compact' : ''}`,
        })}
      >
        <input {...getInputProps()} />
        <ImagePlus className="photo-upload__drop-icon" aria-hidden />
        <span className="photo-upload__drop-title">
          {hasFiles ? 'Tambah file lagi' : 'Drag foto atau klik disini'}
        </span>
        {!hasFiles && enablePaste && <span className="photo-upload__drop-hint">atau tempel dari clipboard (Ctrl+V)</span>}
      </div>

      {(helperText || hint) && (
        <div className="photo-upload__hint-row">
          {helperText && <span className="photo-upload__helper">{helperText}</span>}
          {hint && (
            <span className="photo-upload__error">
              <FileWarning size={12} aria-hidden /> {hint}
            </span>
          )}
        </div>
      )}

      {hasFiles && (
        <ul className="photo-upload__panels">
          {files.map((file, index) => {
            const uid = uidByFile.get(file) ?? `idx_${index}`;
            const pct = progress[uid] ?? 0;
            const status = uploadFn ? statusByFile.get(file) : undefined;
            const done = uploadFn ? status === 'done' : pct >= 100;
            const failed = uploadFn && status === 'failed';
            const actionable = done || failed;
            const errMsg = failed ? errorsByFile.get(file) : null;
            const expanded = showPreview && expandSet.has(index);
            const isRemoving = removing.has(uid);
            const TypeIcon = isImage(file) ? ImageIcon : isVideo(file) ? Film : FileText;
            return (
              <li
                key={uid}
                className={`photo-upload__panel${expanded ? '' : ' photo-upload__panel--collapsed'}${
                  isRemoving ? ' photo-upload__panel--removing' : ''
                }${failed ? ' photo-upload__panel--failed' : ''}`}
              >
                <div className="photo-upload__panel-head">
                  <button
                    type="button"
                    className="photo-upload__panel-head__remove"
                    onClick={() => handleRemove(uid, file)}
                    disabled={disabled || isRemoving}
                    aria-label={`Hapus ${file.name}`}
                  >
                    <X size={14} aria-hidden />
                  </button>
                  <span className="photo-upload__panel-head__icon">
                    <TypeIcon size={15} aria-hidden />
                  </span>
                  <span className="photo-upload__panel-head__meta">
                    <span className="photo-upload__panel-head__name" title={file.name}>
                      {file.name}
                    </span>
                    <span className="photo-upload__panel-head__size">{formatBytes(file.size)}</span>
                  </span>
                  <button
                    type="button"
                    className={`photo-upload__panel-head__action${
                      actionable ? ' photo-upload__panel-head__action--done' : ''
                    }${failed ? ' photo-upload__panel-head__action--failed' : ''}`}
                    onClick={() => handleAction(uid, file)}
                    disabled={disabled || isRemoving}
                    aria-label={actionable ? `Ulangi unggah ${file.name}` : `Mengunggah ${file.name}`}
                    title={failed ? 'Unggah gagal — ulangi' : actionable ? 'Ulangi unggah' : 'Mengunggah…'}
                  >
                    {actionable ? <RotateCw size={15} aria-hidden /> : <ArrowUp size={15} aria-hidden />}
                  </button>
                </div>

                {expanded && (
                  <div className="photo-upload__panel-body">
                    {previews[index] ? (
                      isVideo(file) ? (
                        <video
                          className="photo-upload__panel-media"
                          src={previews[index]}
                          controls
                          muted
                          playsInline
                        />
                      ) : (
                        <img className="photo-upload__panel-media" src={previews[index]} alt={file.name} />
                      )
                    ) : (
                      <span className="photo-upload__panel-placeholder">
                        <TypeIcon size={28} aria-hidden />
                        <span>{file.name}</span>
                      </span>
                    )}
                  </div>
                )}

                <div className="photo-upload__panel-status">
                  {done ? (
                    <span className="photo-upload__panel-status__text photo-upload__panel-status__text--done">
                      <Check size={12} aria-hidden /> Selesai diunggah
                    </span>
                  ) : failed ? (
                    <span className="photo-upload__panel-status__text photo-upload__panel-status__text--failed">
                      <FileWarning size={12} aria-hidden /> Unggah gagal
                      {errMsg ? ` — ${errMsg}` : ''}
                    </span>
                  ) : (
                    <span className="photo-upload__panel-status__text">Mengunggah… {pct}%</span>
                  )}
                  <span className="photo-upload__panel-status__progress">
                    <span style={{ width: `${pct}%` }} />
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
